import { useEffect, useMemo, useRef, useState } from 'react';

import { listWidgetCategories, listWidgetTypes } from '../../../services';
import { widgetTypeIconByRenderer } from '../constants/widgetTypes.js';

const EMPTY = Object.freeze([]);

/**
 * The presentation-type catalog (CT-5196).
 *
 * **The backend owns this vocabulary.** `POST /widget/type/list` publishes the
 * 28 types with their name, category, capabilities and JSON Schemas, and
 * `/widget/create` rejects a `typeCode` that is not in it — which is why the
 * builder no longer carries its own list. Icons stay local: the catalog's
 * `icon` is null, and the tile needs a real component.
 *
 * The two catalogs load together because the grid is grouped by category and a
 * group with no heading is worse than no group.
 */
export const useWidgetTypes = () => {
  const [state, setState] = useState({ types: EMPTY, categories: EMPTY, error: null });
  // Derived, not set: writing state synchronously inside the effect is what
  // `react-hooks/set-state-in-effect` rejects, and the catalog loads once.
  const [resolved, setResolved] = useState(false);
  const isLoading = !resolved;
  const fetchIdRef = useRef(0);

  useEffect(() => {
    const fetchId = ++fetchIdRef.current;
    let mounted = true;

    Promise.all([listWidgetTypes(), listWidgetCategories()])
      .then(([{ types }, { categories }]) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({
          types: types.map((type) => ({
            ...type,
            // The tile renders a component, and the catalog has no icon; the
            // renderer key is the stable name to hang one off.
            icon: widgetTypeIconByRenderer(type.rendererKey),
          })),
          categories,
          error: null,
        });
      })
      .catch((error) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({ types: EMPTY, categories: EMPTY, error });
      })
      .finally(() => {
        if (mounted && fetchId === fetchIdRef.current) setResolved(true);
      });

    return () => {
      mounted = false;
    };
  }, []);

  /** Types grouped in the catalog's own category order, empty groups dropped. */
  const groups = useMemo(
    () =>
      state.categories
        .map((category) => ({
          id: category.key,
          label: category.label,
          description: category.description,
          types: state.types.filter((type) => type.categoryKey === category.key),
        }))
        .filter((group) => group.types.length > 0),
    [state.categories, state.types],
  );

  return useMemo(
    () => ({
      types: state.types,
      categories: state.categories,
      groups,
      isLoading,
      error: state.error,
    }),
    [groups, isLoading, state.categories, state.error, state.types],
  );
};

export default useWidgetTypes;
